import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useQuery } from '@tanstack/react-query';
import { Logo } from '@/components/Logo';
import { termsApi, TermsContentResponse } from '@/services/api/terms';
import { useAuthStore } from '@/store/authStore';
import { ROUTES, SESSION_KEYS } from '@/config/constants';
import { validateToken } from '@/utils/jwt';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
} from '@heroicons/react/24/outline';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface LegalPageProps {
  documentType: 'terms' | 'privacy';
  title: string;
  accentColor: 'blue' | 'green';
}

export function LegalPage({ documentType, title, accentColor }: LegalPageProps) {
  const [activeSection, setActiveSection] = useState<string>('');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const contentRef = useRef<HTMLDivElement>(null);

  // Show a "Dashboard" link instead of "Sign In" when the visitor is logged in.
  // /terms and /privacy are public routes (not under ProtectedRoute), so the auth
  // store isn't hydrated on a fresh full-page load — fall back to the stored token.
  const storeAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const storedToken = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  const isLoggedIn = storeAuthenticated || (storedToken ? validateToken(storedToken).valid : false);

  // Fetch content from API
  const { data: content, isLoading, error } = useQuery<TermsContentResponse>({
    queryKey: ['legal-content', documentType],
    queryFn: () => termsApi.getContent(documentType),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Color classes based on accent color
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      text: 'text-blue-900',
      textLight: 'text-blue-800',
      link: 'text-blue-600 hover:text-blue-800',
      activeNav: 'bg-blue-100 text-blue-900 border-blue-500',
      hoverNav: 'hover:bg-blue-50',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-500',
      text: 'text-green-900',
      textLight: 'text-green-800',
      link: 'text-green-600 hover:text-green-800',
      activeNav: 'bg-green-100 text-green-900 border-green-500',
      hoverNav: 'hover:bg-green-50',
    },
  }[accentColor];

  // Parse table of contents from content
  useEffect(() => {
    if (!content?.content) return;

    const headingRegex = /^(#{1,3})\s+(.+)$/gm;
    const items: TocItem[] = [];
    let match;

    while ((match = headingRegex.exec(content.content)) !== null) {
      const level = match[1].length;
      const text = match[2].replace(/\*\*/g, '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

      // Only include h2 and h3 in TOC
      if (level >= 2 && level <= 3) {
        items.push({ id, text, level });
      }
    }

    setTocItems(items);
  }, [content?.content]);

  // Scroll spy for active section
  useEffect(() => {
    if (!contentRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -66%', threshold: 0 }
    );

    const headings = contentRef.current.querySelectorAll('h2, h3');
    headings.forEach((heading) => observer.observe(heading));

    return () => observer.disconnect();
  }, [content]);

  // Toggle section collapse
  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  // Scroll to section
  const scrollToSection = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Handle PDF download
  const handleDownloadPdf = useCallback(async () => {
    if (!content) return;

    // Log the download
    await termsApi.logDownload({
      document_type: documentType,
      version: content.version,
      download_format: 'pdf',
    });

    // Trigger print dialog (browser will offer "Save as PDF")
    window.print();
  }, [content, documentType]);

  // Handle print
  const handlePrint = useCallback(async () => {
    if (!content) return;

    // Log the download
    await termsApi.logDownload({
      document_type: documentType,
      version: content.version,
      download_format: 'print',
    });

    window.print();
  }, [content, documentType]);

  // Custom markdown components with IDs for scroll navigation
  const markdownComponents = {
    h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const text = String(children);
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return <h1 id={id} className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props}>{children}</h1>;
    },
    h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const text = String(children);
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const isCollapsed = collapsedSections.has(id);

      return (
        <div className="group">
          <h2
            id={id}
            className="text-xl font-semibold text-gray-900 mt-8 mb-4 flex items-center cursor-pointer hover:text-gray-700"
            onClick={() => toggleSection(id)}
            {...props}
          >
            <button className="mr-2 p-1 rounded hover:bg-gray-100">
              {isCollapsed ? (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              )}
            </button>
            {children}
          </h2>
        </div>
      );
    },
    h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => {
      const text = String(children);
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return <h3 id={id} className="text-lg font-medium text-gray-800 mt-6 mb-3" {...props}>{children}</h3>;
    },
    p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
      <p className="text-gray-700 mb-4 leading-relaxed" {...props}>{children}</p>
    ),
    ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul className="list-disc list-inside mb-4 space-y-2 text-gray-700" {...props}>{children}</ul>
    ),
    li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li className="ml-4" {...props}>{children}</li>
    ),
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
      <a href={href} className="text-blue-600 hover:text-blue-800 underline" {...props}>{children}</a>
    ),
    blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
      <blockquote className={`${colorClasses.bg} border-l-4 ${colorClasses.border} p-6 mb-6 rounded-r-lg`} {...props}>
        <div className={`space-y-3 ${colorClasses.textLight}`}>{children}</div>
      </blockquote>
    ),
    hr: () => <hr className="my-8 border-gray-200" />,
    strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <strong className="font-semibold text-gray-900" {...props}>{children}</strong>
    ),
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-red-500">Failed to load content. Please try again.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 print:hidden">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to={isLoggedIn ? '/dashboard' : '/'}
            reloadDocument={!isLoggedIn}
            className="flex items-center"
          >
            <Logo className="h-8 w-auto" />
          </Link>
          <nav className="flex items-center space-x-6 text-sm">
            <Link
              to={documentType === 'terms' ? '/privacy' : '/terms'}
              className="text-gray-600 hover:text-gray-900"
            >
              {documentType === 'terms' ? 'Privacy Policy' : 'Terms of Service'}
            </Link>
            <Link
              to={isLoggedIn ? ROUTES.HOME : ROUTES.LOGIN}
              className="text-blue-600 hover:text-blue-800"
            >
              {isLoggedIn ? 'Dashboard' : 'Sign In'}
            </Link>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-8">
          {/* Sidebar - Sticky Table of Contents */}
          <aside className="hidden lg:block print:hidden">
            <div className="sticky top-24 space-y-6">
              {/* Document Info */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h2 className="font-semibold text-gray-900">{title}</h2>
              </div>

              {/* Actions */}
              <div className="bg-white rounded-lg shadow-sm p-4 space-y-2">
                <button
                  onClick={handleDownloadPdf}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                  Download PDF
                </button>
                <button
                  onClick={handlePrint}
                  className="w-full flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <PrinterIcon className="w-4 h-4 mr-2" />
                  Print
                </button>
              </div>

              {/* Table of Contents */}
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h3 className="font-medium text-gray-900 mb-3">Contents</h3>
                <nav className="space-y-1 max-h-[50vh] overflow-y-auto">
                  {tocItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors ${
                        item.level === 3 ? 'pl-6' : ''
                      } ${
                        activeSection === item.id
                          ? `${colorClasses.activeNav} border-l-2`
                          : `text-gray-600 ${colorClasses.hoverNav}`
                      }`}
                    >
                      {item.text}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="min-w-0">
            <article className="bg-white rounded-lg shadow-sm p-8 md:p-12" ref={contentRef}>
              {/* Document header - the single on-page statement of version and
                  effective date. Not print:hidden, so it appears in the PDF. */}
              <header className="mb-8 pb-6 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
                <p className="mt-2 text-sm text-gray-500">
                  Version {content.version} &middot; Effective {content.effective_date}
                </p>
              </header>

              {/* Mobile-only PDF action (desktop uses the sidebar Actions box) */}
              <div className="lg:hidden mb-6 print:hidden">
                <button
                  onClick={handleDownloadPdf}
                  className="flex items-center px-3 py-1.5 text-sm text-gray-700 border rounded-md hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 mr-1" />
                  PDF
                </button>
              </div>

              {/* Markdown Content */}
              <div className="prose prose-gray max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {content.content}
                </ReactMarkdown>
              </div>
            </article>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-8 print:hidden">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-600">
          <Link to="/terms" className="hover:text-gray-900">Terms & Conditions</Link>
          <span className="mx-3">|</span>
          <Link to="/privacy" className="hover:text-gray-900">Privacy Policy</Link>
          <span className="mx-3">|</span>
          <a href="mailto:support@flouri.ai" className="hover:text-gray-900">Contact</a>
        </div>
      </footer>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            font-size: 12pt;
          }
          .print\\:hidden {
            display: none !important;
          }
          article {
            box-shadow: none !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}
